import {useCallback, useEffect, useRef, useState} from "react";

import type {LocationRequiredDialogProps} from "@/src/modules/core/components/location-required-dialog";
import {tryAcquireReloadLock} from "@/src/modules/core/lib/app-reload-coordinator";
import {
  getGeolocationPlatform,
  requiresReloadToRecoverLocation,
} from "@/src/modules/core/lib/geolocation-platform";
import {markLocationGateRetryPending} from "@/src/modules/core/lib/location-gate-resume";
import {verifyLocationForRequiredSheet} from "@/src/modules/core/lib/verify-location-for-sheet";

type OpenAction = () => void;

/** Only show the New-button spinner if the gate check exceeds this (avoids flash on fast path). */
const LOCATION_CHECK_SPINNER_DELAY_MS = 600;

export type RequestLocationGatedOpenOptions = {
  /** Background opens (e.g. draft resume) must not spin the New button. */
  silent?: boolean;
};

export type LocationGatedOpenDialogProps = Pick<
  LocationRequiredDialogProps,
  "open" | "isRechecking" | "showReloadHint" | "onRecheck" | "onDismiss"
>;

type RecheckOptions = {
  /** Auto-retry when returning from Settings — no button spinner, no auto-reload. */
  silent?: boolean;
};

/**
 * Runs a location probe before opening coordinate-required sheets.
 * If blocked, expose dialog props instead of mounting the sheet.
 */
export function useLocationGatedOpen() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isCheckingLocation, setIsCheckingLocation] = useState(false);
  const [isRechecking, setIsRechecking] = useState(false);
  const [showReloadHint, setShowReloadHint] = useState(false);
  const pendingOpenRef = useRef<OpenAction | null>(null);
  const recheckInFlightRef = useRef(false);

  const runPendingOpen = useCallback(() => {
    const openAction = pendingOpenRef.current;
    pendingOpenRef.current = null;
    openAction?.();
  }, []);

  const reloadApp = useCallback(() => {
    if (!tryAcquireReloadLock()) {
      return;
    }
    window.location.reload();
  }, []);

  const dismissDialog = useCallback(() => {
    pendingOpenRef.current = null;
    setDialogOpen(false);
    setShowReloadHint(false);
    setIsRechecking(false);
  }, []);

  const completeRecheckSuccess = useCallback(() => {
    setDialogOpen(false);
    setShowReloadHint(false);
    runPendingOpen();
  }, [runPendingOpen]);

  const handleRecheck = useCallback(
    async (options: RecheckOptions = {}) => {
      if (recheckInFlightRef.current) {
        return;
      }

      recheckInFlightRef.current = true;
      if (!options.silent) {
        setIsRechecking(true);
      }

      try {
        // iOS standalone cannot recover permission in-session — reload immediately.
        if (!options.silent && requiresReloadToRecoverLocation()) {
          markLocationGateRetryPending();
          reloadApp();
          return;
        }

        const allowed = await verifyLocationForRequiredSheet({forceProbe: true});
        if (allowed) {
          completeRecheckSuccess();
          return;
        }

        const platform = getGeolocationPlatform();
        if (platform === "ios" || platform === "android") {
          setShowReloadHint(true);
        }
      } finally {
        recheckInFlightRef.current = false;
        if (!options.silent) {
          setIsRechecking(false);
        }
      }
    },
    [completeRecheckSuccess, reloadApp],
  );

  const requestOpen = useCallback(
    async (openAction: OpenAction, options: RequestLocationGatedOpenOptions = {}) => {
      pendingOpenRef.current = openAction;
      let spinnerTimer: ReturnType<typeof setTimeout> | undefined;

      if (!options.silent) {
        spinnerTimer = setTimeout(() => {
          setIsCheckingLocation(true);
        }, LOCATION_CHECK_SPINNER_DELAY_MS);
      }

      try {
        const allowed = await verifyLocationForRequiredSheet();
        if (allowed) {
          pendingOpenRef.current = null;
          openAction();
          return;
        }

        setShowReloadHint(false);
        setDialogOpen(true);
      } finally {
        if (spinnerTimer) {
          clearTimeout(spinnerTimer);
        }
        if (!options.silent) {
          setIsCheckingLocation(false);
        }
      }
    },
    [],
  );

  /** Returning from Settings often fixes permission before the user taps recheck. */
  useEffect(() => {
    // iOS standalone can't recover in-session — recheck would be wasted work.
    if (!dialogOpen || requiresReloadToRecoverLocation()) {
      return;
    }

    const tryAutoRecheck = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      void handleRecheck({silent: true});
    };

    window.addEventListener("pageshow", tryAutoRecheck);
    document.addEventListener("visibilitychange", tryAutoRecheck);

    return () => {
      window.removeEventListener("pageshow", tryAutoRecheck);
      document.removeEventListener("visibilitychange", tryAutoRecheck);
    };
  }, [dialogOpen, handleRecheck]);

  const locationDialogProps: LocationGatedOpenDialogProps = {
    open: dialogOpen,
    isRechecking,
    showReloadHint,
    onRecheck: () => {
      void handleRecheck();
    },
    onDismiss: dismissDialog,
  };

  return {
    isCheckingLocation,
    requestOpen,
    locationDialogProps,
  };
}
